import { faChevronLeft, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useRef } from 'react';
import PAGINATION from '../../utils/PaginacaoUtils';
import './TabelaPadrao.css';
import BotaoEditar from '../BotaoEditar/BotaoEditar';
import { validarPermissoes } from '../../utils/PermissaoUtils';
import FormatacaoUtils from '../../utils/FormatacaoUtils';
import BotaoAdicionarVinculos from '../BotaoAdicionarVinculos';
import Tooltip from '../Tooltip/Tooltip';
import { MdEdit } from 'react-icons/md';

const TabelaPadrao = ({
  dados = [],
  colunas = [],
  totalItems = 0,
  paginaAtual = 0,
  tamanhoPagina = PAGINATION.DEFAULT_PAGE_SIZE,
  onEditar = null,
  onProximaPagina,
  onPaginaAnterior,
  onIrParaPagina,
  colunasFixas = [], // Define quais colunas são sempre visíveis (pelo índice)
  breakpoints = { // Define os breakpoints para ocultar colunas gradualmente
    pequeno: 576,
    medio: 768,
    grande: 992,
  },
  gruposColunas = [], // Grupos de colunas para cabeçalho em dois níveis: { titulo, indices: [i1, i2, ...] }
  expansivo = false, // pode ser boolean ou função (item) => boolean
  renderExpansao, // função (item) => JSX para conteúdo de expansão
  permissaoEditar, // permissão para editar
  colunasPermissao = {}, // NOVO: objeto { campo: boolean } para controle de visibilidade
  // Props para funcionamento do checkbox
  acaoPersonalizada = null, // Função que recebe o item e retorna o componente para ação personalizada
  acaoCheckbox = false, // Habilitar checkbox nas linhas
  selecionados = new Map(), // Map com os itens selecionados
  onSelecionar = null, // Função chamada ao selecionar um item (recebe o novo Map atualizado)
  componenteCheckboxCabecalho = null, // Componente a ser exibido no cabeçalho da coluna de checkbox
  acaoCabecalho = null, // Componente para o cabeçalho da coluna de ação (ex: botão selecionar todos)
  getItemId = (item) => item.id, // Função para obter o ID do item para uso no Map de seleção
  getItemDescription = (item) => item.descricao || item.nome, // Função para obter a descrição do item para o Map
  linhaDestaque = null, // Função (item) => boolean que determina se a linha deve ser destacada
  desabilitarEstiloBotaoEditar = false,
  ultimosAdicionadosIds = [],
  loading = false,
  iconeAcao = <MdEdit />,
  tooltipMessage = 'Editar'
}) => {
  // Estados
  const [expandedRows, setExpandedRows] = useState({});
  const [renderedRows, setRenderedRows] = useState({});
  const [expansaoAtiva, setExpansaoAtiva] = useState({});
  const [paginaInput, setPaginaInput] = useState(paginaAtual + 1);

  const toggleRow = idx => {
    setExpandedRows(prev => {
      const isExpanding = !prev[idx];
      if (isExpanding) {
        setRenderedRows(r => ({ ...r, [idx]: true }));
      }
      return { ...prev, [idx]: isExpanding };
    });
  };

  // Remove conteúdo do DOM após animação de colapso
  useEffect(() => {
    const closingRows = Object.entries(expandedRows)
      .filter(([idx, expanded]) => !expanded && renderedRows[idx])
      .map(([idx]) => idx);
    if (closingRows.length > 0) {
      const timeout = setTimeout(() => {
        setRenderedRows(r => {
          const novo = { ...r };
          closingRows.forEach(idx => { delete novo[idx]; });
          return novo;
        });
      }, 500); // igual ao tempo da transição do CSS
      return () => clearTimeout(timeout);
    }
  }, [expandedRows, renderedRows]);

  // Atualiza a classe expandido para animação suave
  useEffect(() => {
    Object.keys(renderedRows).forEach(idx => {
      if (renderedRows[idx] && expandedRows[idx] && !expansaoAtiva[idx]) {
        setTimeout(() => {
          setExpansaoAtiva(prev => ({ ...prev, [idx]: true }));
        }, 10);
      }
      if (!expandedRows[idx] && expansaoAtiva[idx]) {
        setExpansaoAtiva(prev => ({ ...prev, [idx]: false }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedRows, expandedRows]);

  const totalPaginas = Math.ceil(totalItems / tamanhoPagina);
  const [larguraTela, setLarguraTela] = useState(window.innerWidth);

  // Detecta o tamanho da tela para ajustar a exibição de colunas
  useEffect(() => {
    const handleResize = () => {
      setLarguraTela(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Atualiza o valor do input de página quando a página atual muda
  useEffect(() => {
    setPaginaInput(paginaAtual + 1);
    valorAnteriorRef.current = paginaAtual + 1;
    foiEditadoRef.current = false;
  }, [paginaAtual]);

  const colunasVisiveis = () => {
    // NOVO: filtra colunas conforme colunasPermissao
    let filtradas = colunas;
    if (colunasPermissao && Object.keys(colunasPermissao).length > 0) {
      filtradas = colunas.filter(col => {
        if (!col.campo) return true;
        // Suporte para campos aninhados (ex: clienteDTO.empresaDTO.razaoSocial)
        const campoBase = col.campo.split('.')[0] + (col.campo.split('.').length > 1 ? '.' + col.campo.split('.')[1] : '');
        if (colunasPermissao[col.campo] === false || colunasPermissao[campoBase] === false) return false;
        return true;
      });
    }
    // ...lógica de breakpoints existente...
    if (larguraTela >= breakpoints.grande) {
      return filtradas;
    }
    if (larguraTela >= breakpoints.medio) {
      return filtradas.filter((_, index) => {
        return colunasFixas.includes(index) || index < Math.ceil(filtradas.length * 0.7);
      });
    }
    if (larguraTela >= breakpoints.pequeno) {
      return filtradas.filter((_, index) => {
        return colunasFixas.includes(index) || index < Math.ceil(filtradas.length * 0.5);
      });
    }
    return filtradas.filter((_, index) => colunasFixas.includes(index));
  };

  const hasPermissaoEditar = (colunaPermissao) => {
    if (!colunaPermissao) return true;
    return validarPermissoes(colunaPermissao);
  };

  // Renderização de cabeçalho em dois níveis quando gruposColunas definido
  const renderHeaderGrupos = () => {
    const colunasExibidas = colunasVisiveis();
    const skip = new Set();
    const header1 = [];
    const header2 = [];
    // Botão de edição
    if (onEditar && hasPermissaoEditar(permissaoEditar)) header1.push(
      <th key="acao-group" rowSpan={2} className="coluna-acao">Ação</th>
    );
    
    // Coluna checkbox
    if (acaoCheckbox) header1.push(
      <th key="checkbox-group" rowSpan={2} className="coluna-checkbox">
        {componenteCheckboxCabecalho}
      </th>
    );
    // construir headers com visibilidade
    colunas.forEach((col, idx) => {
      const grupo = gruposColunas.find(g => g.indices[0] === idx);
      if (grupo) {
        // colunas visíveis no grupo
        const visCount = grupo.indices.filter(i => colunasExibidas.includes(colunas[i])).length;
        header1.push(
          <th
            key={'g' + idx}
            colSpan={visCount}
            className={visCount ? '' : 'coluna-oculta'}
          >
            {grupo.titulo}
          </th>
        );
        grupo.indices.forEach(i => skip.add(i));
      } else if (!skip.has(idx)) {
        const isVisible = colunasExibidas.includes(col);
        const cssClasses = [col.className, isVisible ? '' : 'coluna-oculta'].filter(Boolean).join(' ');
        header1.push(
          <th
            key={idx}
            rowSpan={2}
            className={cssClasses}
            style={{ width: col.largura, textAlign: col.alignHeader }}
          >
            {col.titulo}
          </th>
        );
      }
    });
    // coluna de expansão sempre visível
    if (expansivo) header1.push(
      <th key="expand-group" rowSpan={2} className="coluna-expandir"> </th>
    );
    // segunda linha: sub-colunas com visibilidade
    colunas.forEach((col, idx) => {
      if (gruposColunas.some(g => g.indices.includes(idx))) {
        const isVisible = colunasExibidas.includes(col);
        header2.push(
          <th key={'s' + idx} className={isVisible ? '' : 'coluna-oculta'} style={{ width: col.largura, textAlign: col.alignHeader }}>
            {col.titulo}
          </th>
        );
      }
    });
    return (
      <>
        <tr>{header1}</tr>
        <tr>{header2}</tr>
      </>
    );
  };

  const renderHeader = () => {
    const colunasExibidas = colunasVisiveis();

    return (
      <tr>
        {/* Botão de edição no cabeçalho */}
        {onEditar && hasPermissaoEditar(permissaoEditar) && (
          <th className="coluna-acao">Ação</th>
        )}
        {/* Checkbox no cabeçalho */}
        {acaoCheckbox && (
          <th className="coluna-checkbox">
            {componenteCheckboxCabecalho}
          </th>
        )}
        {/* Coluna para ações personalizadas */}
        {acaoPersonalizada && !acaoCheckbox && (
          <th className="coluna-acao-personalizada">
            {acaoCabecalho || "Ação"}
          </th>
        )}
        {colunas.map((coluna, index) => (
          <th
            key={index}
            style={{ width: coluna.largura, textAlign: coluna.alignHeader }}
            className={`${coluna.className || ''} coluna-${index} ${
              colunasExibidas.includes(coluna) ? '' : 'coluna-oculta'
            }`}
          >
            {coluna.titulo}
          </th>
        ))}
        {expansivo && <th className="coluna-expandir"></th>}
      </tr>
    );
  };

  const renderCelula = (item, coluna, index) => {
    const colunasExibidas = colunasVisiveis();
    const classeBase = coluna.className || '';
    const classeVisibilidade = colunasExibidas.includes(coluna) ? '' : 'coluna-oculta';

    // Renderização especial para status
    if (coluna.campo === 'situacao' || coluna.campo === 'status') {
      const valor = coluna.render ? coluna.render(item) : item[coluna.campo];
      const statusClass = valor === 'ativo' ? 'status-ativo' : 'status-inativo';

      return (
        <td className={`status coluna-${index} ${classeVisibilidade}`}>
          <span className={statusClass}>{valor}</span>
        </td>
      );
    }

    // Formatação automática para campos que contenham 'documento'
    let valorCelula = coluna.render ? coluna.render(item) : item[coluna.campo];
    if (coluna.campo && String(coluna.campo).toLowerCase().includes('documento')) {
      valorCelula = FormatacaoUtils.formatarDocumento(valorCelula);
    }

    // Verificar se é uma célula que pode ter tooltip (células com limitação de tamanho)
    const isColunaTruncavel = !['coluna-acao', 'coluna-checkbox', 'coluna-acao-personalizada', 'coluna-expansao', 'status'].some(
      classe => classeBase.includes(classe) || `coluna-${index}`.includes('acao') || `coluna-${index}`.includes('checkbox')
    );

    // Se é uma coluna truncável e tem conteúdo, aplicar tooltip
    if (isColunaTruncavel && valorCelula && String(valorCelula).length > 25) {
      return (
        <td className={`${classeBase} coluna-${index} ${classeVisibilidade}`}>
          <Tooltip text={String(valorCelula)} lado="ESQUERDA">
            <span>{valorCelula}</span>
          </Tooltip>
        </td>
      );
    }

    return (
      <td 
      style={{ width: coluna.largura }}
      className={`${classeBase} coluna-${index} ${classeVisibilidade}`}>
        {valorCelula}
      </td>
    );
  };

  const renderLinhas = () => {
    if (!dados || dados.length === 0) {
      // Calcula o total de colunas considerando todas as ações
      const totalColunas = colunas.length 
        + (onEditar ? 1 : 0) 
        + (acaoCheckbox ? 1 : 0) 
        + (acaoPersonalizada ? 1 : 0) 
        + (expansivo ? 1 : 0);
      
      return (
        <tr>
          <td colSpan={totalColunas} className="sem-dados">
            Nenhum registro encontrado
          </td>
        </tr>
      );
    }

    return dados.map((item, indexLinha) => {
      // Suporte a expansivo por linha
      const isExpansivo = typeof expansivo === 'function' ? expansivo(item) : expansivo;
      let rowClass = '';
      if (isExpansivo) {
        rowClass = 'linha-clicavel';
        if (expandedRows[indexLinha]) rowClass += ' expandido';
      }
      return (
        <React.Fragment key={item.idCliente || indexLinha}>
          <tr 
            className={`${rowClass} ${linhaDestaque && linhaDestaque(item) ? 'linha-destacada' : ''}`} 
            onClick={isExpansivo ? () => toggleRow(indexLinha) : undefined}
          >
            {/* Botão de editar padrão */}
            {onEditar && hasPermissaoEditar(permissaoEditar) && (
              <td className="coluna-acao">
                <BotaoEditar onClick={onEditar} info={item} icone={iconeAcao} tooltip={tooltipMessage} />
              </td>
            )}

            {/* Botão para seleção múltipla ou ação personalizada */}
            {acaoCheckbox ? (
              <td className="coluna-checkbox">
                <BotaoAdicionarVinculos
                  adicionar={!selecionados.has(getItemId(item))}
                  onClick={() => {
                    const novoSelecionados = new Map(selecionados);
                    if (novoSelecionados.has(getItemId(item))) {
                      novoSelecionados.delete(getItemId(item));
                    } else {
                      novoSelecionados.set(getItemId(item), 
                        typeof getItemDescription === 'function' 
                          ? { id: getItemId(item), descricao: getItemDescription(item) }
                          : item
                      );
                    }
                    onSelecionar && onSelecionar(novoSelecionados);
                  }}
                />
              </td>
            ) : acaoPersonalizada ? (
              <td className="coluna-acao-personalizada">
                {acaoPersonalizada(item)}
              </td>
            ) : null}
            {colunas.map((coluna, indexColuna) => (
              <React.Fragment key={`${indexLinha}-${indexColuna}`}>
                {renderCelula(item, coluna, indexColuna)}
              </React.Fragment>
            ))}
            {isExpansivo && (
              <td className="coluna-expansao">
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`icone-expansao ${expandedRows[indexLinha] ? 'expandido' : ''}`}
                />
              </td>
            )}
          </tr>
          {isExpansivo && renderedRows[indexLinha] && (
            <tr className="linha-expansiva" onClick={() => toggleRow(indexLinha)}>
              <td colSpan={colunas.length + (onEditar ? 1 : 0) + 1}>
                <div
                  className={`expansao-content${expansaoAtiva[indexLinha] ? ' expandido' : ''}`}
                  onClick={e => e.stopPropagation()}
                >
                  {renderExpansao(item)}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  // Referência para rastrear se o valor foi realmente alterado pelo usuário
  const inputRef = useRef(null);
  const valorAnteriorRef = useRef(paginaAtual + 1);
  const foiEditadoRef = useRef(false);
  
  // Atualiza o valor do input quando a página atual muda externamente
  useEffect(() => {
    setPaginaInput(paginaAtual + 1);
    foiEditadoRef.current = false;
    valorAnteriorRef.current = paginaAtual + 1;
  }, [paginaAtual]);
  
  // Função para lidar com a mudança no campo de entrada da paginação
  const handleInputChange = (e) => {
    // Marca que o usuário editou o valor
    foiEditadoRef.current = true;
    // Apenas atualiza o valor do campo sem navegar
    setPaginaInput(e.target.value);
  };
  
  // Função para lidar com a navegação quando o Enter é pressionado
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const novaPagina = parseInt(paginaInput, 10);
      // Só navega se o valor for válido e diferente da página atual
      if (!isNaN(novaPagina) && novaPagina > 0 && novaPagina <= totalPaginas && (novaPagina - 1) !== paginaAtual) {
        onIrParaPagina(novaPagina - 1);
      } else {
        // Restaura o valor atual se for inválido ou igual à página atual
        setPaginaInput(paginaAtual + 1);
      }
      // Reseta o flag de edição
      foiEditadoRef.current = false;
      valorAnteriorRef.current = paginaAtual + 1;
      // Remove o foco do input para melhorar a experiência do usuário
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };
  
  // Função para lidar com a perda de foco
  const handleBlur = () => {
    // Só navegamos se o usuário realmente editou o valor
    if (foiEditadoRef.current) {
      const novaPagina = parseInt(paginaInput, 10);
      // Verifica se o valor é válido e diferente da página atual
      if (!isNaN(novaPagina) && novaPagina > 0 && novaPagina <= totalPaginas && (novaPagina - 1) !== paginaAtual) {
        onIrParaPagina(novaPagina - 1);
      } else {
        // Se for um valor inválido ou igual à página atual, volta para o valor atual
        setPaginaInput(paginaAtual + 1);
      }
    } else {
      // Garante que o valor seja sempre o correto mesmo quando apenas clicado
      setPaginaInput(paginaAtual + 1);
    }
    
    // Reseta os flags independente do caso
    foiEditadoRef.current = false;
    valorAnteriorRef.current = paginaAtual + 1;
  };
  
  // Função para lidar com o clique no input
  const handleInputClick = (e) => {
    // Impede a propagação do evento de clique
    e.stopPropagation();
    // NÃO previne o evento default para manter o comportamento padrão do input
    // e.preventDefault(); 
    
    // Garante que o valor no input é o valor atual correto
    if (paginaInput !== paginaAtual + 1) {
      setPaginaInput(paginaAtual + 1);
    }
    
    // Seleciona todo o texto do input para facilitar a edição
    e.target.select();
    
    // Indica que ainda não foi editado neste ciclo de clique
    foiEditadoRef.current = false;
  };
  
  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;

    return (
      <div className="controles-paginacao">
        <div className="paginacao-info">
          <span>Página</span>
          <input
            ref={inputRef}
            type="number"
            className="pagina-atual-input"
            value={paginaInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            min="1"
            max={totalPaginas}
            onClick={handleInputClick}
            onMouseDown={(e) => {
              // Isso previne a seleção automática que pode causar mudanças visuais
              if (paginaInput === (paginaAtual + 1).toString()) {
                e.target.select();
              }
            }}
          />
          <span>de {totalPaginas}</span>
        </div>

        <button onClick={onPaginaAnterior} disabled={paginaAtual === 0} className="botao-navegacao">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        <button
          onClick={onProximaPagina}
          disabled={paginaAtual >= totalPaginas - 1}
          className="botao-navegacao"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    );
  };

  const renderContagemItens = () => {
    return (
      <div className="contagem-itens">
        {totalItems > 0 ? (
          <span>
            {paginaAtual * tamanhoPagina + 1} -{' '}
            {Math.min((paginaAtual + 1) * tamanhoPagina, totalItems)} de {totalItems}
          </span>
        ) : (
          <span>0 registros</span>
        )}
      </div>
    );
  };

  return (
    <div className="tabela-padrao-container">
      <div className="tabela-cabecalho">
        {renderContagemItens()}
        {renderPaginacao()}
      </div>

      <div className="tabela-responsive">
        <table className="tabela-padrao">
          <thead>
            {gruposColunas && gruposColunas.length > 0 ? renderHeaderGrupos() : renderHeader()}
          </thead>
          <tbody>{renderLinhas()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default TabelaPadrao;